import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PostCard from "@/components/feature/PostCard";
import {
  formatDate,
  formatViews,
  getCategoryBySlug,
  getPostById,
  getRelatedPosts,
} from "@/lib/content";
import { toneBadge } from "@/lib/tone";

export default function PostPage() {
  const { id = "" } = useParams();
  const post = getPostById(id);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!post) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
          <i className="ri-file-unknow-line text-5xl text-primary-400"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇内容走丢了</h1>
          <p className="mt-3 text-sm text-foreground-600">链接可能已经更新，去首页挑一篇新的看看。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const category = getCategoryBySlug(post.categorySlug);
  const categoryName = category?.name ?? "内容";
  const tone = category?.tone ?? "primary";
  const related = getRelatedPosts(post, 3);
  const toc = post.content
    .map((block, index) => ({ block, index }))
    .filter((item) => item.block.type === "h2");
  const likeCount = liked ? post.likes + 1 : post.likes;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      dateModified: post.date,
      inLanguage: "zh-CN",
      articleSection: categoryName,
      keywords: post.tags.join(","),
      image: [post.cover],
      author: { "@type": "Person", name: post.author },
      publisher: { "@type": "Organization", name: "瓜乐圈", url: SITE_URL },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/post/${post.id}` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: categoryName, item: `${SITE_URL}/category/${post.categorySlug}` },
        { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_URL}/post/${post.id}` },
      ],
    },
  ];

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(`${SITE_URL}/post/${post.id}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title={`${post.title} | 瓜乐圈`}
        description={post.excerpt}
        keywords={post.tags.join(",")}
        path={`/post/${post.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
        <article>
          <section className="border-b border-background-200 bg-background-100">
            <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
              <Breadcrumb
                items={[
                  { label: "首页", to: "/" },
                  { label: categoryName, to: `/category/${post.categorySlug}` },
                  { label: post.title },
                ]}
              />
              <div className="mt-6 max-w-3xl">
                <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${toneBadge[tone]}`}>
                  <i className="ri-price-tag-3-line"></i>
                  {categoryName}
                </span>
                <h1 className="mt-4 font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-4xl">
                  {post.title}
                </h1>
                <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{post.excerpt}</p>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-3-line"></i>
                    {post.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {formatDate(post.date)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    {post.readMinutes} 分钟读完
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i>
                    {formatViews(post.views)} 次浏览
                  </span>
                </div>
              </div>
            </div>
          </section>

          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <div className="overflow-hidden rounded-xl2 border border-background-200 bg-background-100">
                  <img src={post.cover} alt={post.title} className="h-56 w-full object-cover object-top md:h-80" />
                </div>

                <div className="mt-8 rounded-xl2 border border-accent-200 bg-accent-50 p-5 md:p-6">
                  <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-accent-950">
                    <i className="ri-flashlight-line"></i>
                    要点速览
                  </h2>
                  <ul className="mt-3 space-y-2.5">
                    {post.highlights.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-accent-900">
                        <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[7px] text-accent-600"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8 space-y-6">
                  {post.content.map((block, index) => {
                    if (block.type === "h2") {
                      return (
                        <h2
                          key={`h2-${index}`}
                          id={`section-${index}`}
                          className="scroll-mt-24 pt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl"
                        >
                          {block.text}
                        </h2>
                      );
                    }
                    if (block.type === "quote") {
                      return (
                        <blockquote
                          key={`quote-${index}`}
                          className="border-l-4 border-primary-400 bg-background-100 px-5 py-4 font-heading text-base leading-relaxed text-foreground-800 md:text-lg"
                        >
                          {block.text}
                        </blockquote>
                      );
                    }
                    if (block.type === "list") {
                      return (
                        <ul key={`list-${index}`} className="space-y-3">
                          {block.items.map((item) => (
                            <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-foreground-700">
                              <i className="ri-arrow-right-s-fill mt-1 text-primary-500"></i>
                              {item}
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    return (
                      <p key={`p-${index}`} className="text-[15px] leading-loose text-foreground-700 md:text-base">
                        {block.text}
                      </p>
                    );
                  })}
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-3">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-200 bg-background-100 px-3 py-1.5 text-xs text-foreground-600"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
                  <button
                    type="button"
                    onClick={() => setLiked((value) => !value)}
                    className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                      liked
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
                    }`}
                  >
                    <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                    {liked ? "已点赞" : "点个赞"} · {formatViews(likeCount)}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-secondary-400 hover:text-secondary-700"
                  >
                    <i className={copied ? "ri-check-line" : "ri-link"}></i>
                    {copied ? "链接已复制" : "复制链接"}
                  </button>
                  <Link
                    to={`/category/${post.categorySlug}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                  >
                    同栏目内容
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>

                <div className="mt-8 flex flex-col gap-4 rounded-xl2 border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-lg font-bold text-background-50">
                    {post.author.slice(-1)}
                  </span>
                  <div>
                    <p className="font-heading text-sm font-bold text-foreground-950">{post.author}</p>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                      负责「{categoryName}」栏目的内容整理与点评，擅长把热闹的场面拆成看得懂的细节。
                    </p>
                  </div>
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24">
                  {toc.length > 0 && (
                    <div className="rounded-xl2 border border-background-200 bg-background-50 p-5">
                      <h2 className="font-heading text-sm font-bold text-foreground-950">内容目录</h2>
                      <ul className="mt-3 space-y-2.5">
                        {toc.map((item) => (
                          <li key={`toc-${item.index}`}>
                            <a
                              href={`#section-${item.index}`}
                              className="flex items-start gap-2 text-sm leading-snug text-foreground-600 transition-colors hover:text-primary-700"
                            >
                              <i className="ri-links-line mt-0.5 text-foreground-300"></i>
                              {item.block.type === "h2" ? item.block.text : ""}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-6 rounded-xl2 border border-background-200 bg-background-50 p-5">
                    <h2 className="font-heading text-sm font-bold text-foreground-950">相关推荐</h2>
                    <div className="mt-3 divide-y divide-background-200">
                      {related.map((item) => (
                        <div key={item.id} className="py-1">
                          <PostCard post={item} variant="compact" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 rounded-xl2 border border-primary-200 bg-primary-50 p-5">
                    <p className="font-heading text-sm font-bold text-primary-800">想看更多乐子？</p>
                    <p className="mt-2 text-xs leading-relaxed text-primary-900/80">
                      订阅每周周报，我们把这一周最值得看的内容整理好发给你。
                    </p>
                    <Link
                      to="/#newsletter"
                      className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      去订阅
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </aside>
            </div>

            <section className="mt-14 border-t border-background-200 pt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">继续看这些</h2>
              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <PostCard key={`related-${item.id}`} post={item} />
                ))}
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}